/**
 * AddCurrencyDialog.tsx
 * Small dialog to add a new currency row to the current-exchange-rate table.
 *
 * The admin enters a 3-letter ISO 4217 code and a rate-to-CAD. On submit the
 * page persists it via setCurrentRate(). CAD and already-present currencies are
 * rejected client-side before hitting the API.
 */

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { BASE_CURRENCY, normalizeCurrencyCode } from './exchangeRateConstants';

interface AddCurrencyDialogProps {
  open: boolean;
  /** Currencies already shown in the table (to block duplicates). */
  existing: string[];
  /** Disables the submit button while a save is in flight. */
  saving: boolean;
  onClose: () => void;
  onSubmit: (currency: string, rateToCAD: number) => void;
}

export const AddCurrencyDialog = ({
  open,
  existing,
  saving,
  onClose,
  onSubmit,
}: AddCurrencyDialogProps) => {
  const [code, setCode] = useState('');
  const [rate, setRate] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCode('');
      setRate('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = () => {
    const currency = normalizeCurrencyCode(code);
    if (!currency) {
      setError('Currency must be a 3-letter ISO 4217 code (e.g. USD, EUR)');
      return;
    }
    if (currency === BASE_CURRENCY) {
      setError('CAD is the base currency (1.0) and cannot be added');
      return;
    }
    if (existing.map((c) => c.toUpperCase()).includes(currency)) {
      setError(`${currency} is already in the table`);
      return;
    }
    const rateNum = Number(rate.trim());
    if (!Number.isFinite(rateNum)) {
      setError('rateToCAD must be a finite number');
      return;
    }
    if (rateNum <= 0) {
      setError('rateToCAD must be greater than 0');
      return;
    }
    if (rateNum > 100000) {
      setError('rateToCAD must be at most 100000');
      return;
    }
    setError(null);
    onSubmit(currency, rateNum);
  };

  const upperCode = code.trim().toUpperCase() || 'XXX';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Add currency</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Currency code"
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase().slice(0, 3)); setError(null); }}
            size="small"
            fullWidth
            placeholder="AUD"
            inputProps={{ maxLength: 3 }}
            helperText="ISO 4217 code — exactly 3 letters"
          />
          <TextField
            label="Rate to CAD"
            value={rate}
            onChange={(e) => { setRate(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !saving) { e.preventDefault(); handleSubmit(); } }}
            type="number"
            size="small"
            fullWidth
            inputProps={{ step: '0.0001', min: '0' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">1 {upperCode} =</InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">CAD</InputAdornment>,
            }}
            helperText="How many CAD equal 1 unit of this currency"
          />
          {error && <Alert severity="error" sx={{ fontSize: '0.82rem' }}>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {saving ? 'Adding…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
